import { createEffect, createSignal, onCleanup, onMount } from "solid-js";
import * as d3 from "d3";
import { sourcesStore } from "../../store/sourcesStore";
import { sanitizeTooltipHtml } from "../../utils/htmlSanitization";
import type {
  FleetRankingBucket,
  FleetRankingDisplayMode,
  FleetRankingGroupBy,
  FleetRankingSeries,
  FleetRankingSeriesPoint,
} from "../../reports/gp50/helpers/fleetRankingTrendApi";
import {
  hasEnoughFleetRankingSamples,
  fleetRankingGroupByAxisLabel,
  fleetRankingGroupByHeader as fleetRankingGroupByTooltipHeader,
} from "../../reports/gp50/helpers/fleetRankingTrendApi";
import type { FleetRankingRankDirection } from "../../reports/gp50/helpers/fleetRankingPrestartApi";

export interface FleetRankingTrendChartProps {
  buckets: FleetRankingBucket[];
  series: FleetRankingSeries[];
  displayMode: FleetRankingDisplayMode;
  groupBy: FleetRankingGroupBy;
  title: string;
  selectedSourceNames?: string[];
  showLegend?: boolean;
  dimOpacity?: number;
  /** Y-axis / tooltip label for value display (default: VMG %). */
  valueAxisLabel?: string;
  /** Tooltip row label for metric value in ranking mode (default: VMG %). */
  valueTooltipLabel?: string;
  /** Ranking direction when computing ranks externally; used for value-axis hint only. */
  rankDirection?: FleetRankingRankDirection;
  /** When true, value-mode y-axis includes negative values (e.g. signed prestart distances). */
  valueAxisAllowNegative?: boolean;
  /** Value-mode y-axis upper cap; plotted values above this are clipped. */
  valueAxisMax?: number;
  /** Value-mode y-axis lower cap; plotted values below this are clipped. */
  valueAxisMin?: number;
  /** Minimum sample count required to draw a point. Defaults to fleet ranking threshold. */
  minSampleCount?: number;
  /** When set, x-axis labels are clickable (e.g. event drill-down). */
  onBucketClick?: (bucketKey: string, bucketLabel: string) => void;
  /**
   * Ranking y values are already an average of per-start places, not a single place in the bucket.
   * The axis and tooltip say so, and a fractional place is kept.
   */
  meanRank?: boolean;
}

const DEFAULT_DIM_OPACITY = 0.18;

interface TooltipState {
  visible: boolean;
  content: string;
  x: number;
  y: number;
}

type SetTooltip = (state: TooltipState) => void;

function sourceColor(sourceName: string): string {
  return sourcesStore.getSourceColor(sourceName) || "#1f77b4";
}

const GLOW_SOURCE_CODES = new Set(["NZL", "USA"]);

function needsLightGlow(sourceName: string): boolean {
  return GLOW_SOURCE_CODES.has(sourceName.trim().toUpperCase());
}

function seriesOpacity(
  sourceName: string,
  selectedSourceNames: string[] | undefined,
  dimOpacity: number
): number {
  if (!selectedSourceNames || selectedSourceNames.length === 0) return 1;
  const normalized = sourceName.trim().toLowerCase();
  const isSelected = selectedSourceNames.some(
    (name) => name.trim().toLowerCase() === normalized
  );
  return isSelected ? 1 : dimOpacity;
}

function formatYTick(value: d3.NumberValue, displayMode: FleetRankingDisplayMode): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  if (displayMode === "ranking") return String(Math.round(num));
  return d3.format(".0f")(num);
}

function clipPlotYValue(
  y: number,
  valueAxisMin: number | undefined,
  valueAxisMax: number | undefined
): number {
  let clipped = y;
  if (valueAxisMin != null && Number.isFinite(valueAxisMin)) {
    clipped = Math.max(clipped, valueAxisMin);
  }
  if (valueAxisMax != null && Number.isFinite(valueAxisMax)) {
    clipped = Math.min(clipped, valueAxisMax);
  }
  return clipped;
}

function hasEnoughSamplesForChart(sampleCount: number, minSampleCount: number | undefined): boolean {
  if (minSampleCount == null) return hasEnoughFleetRankingSamples(sampleCount);
  return sampleCount >= minSampleCount;
}

function formatMeanRank(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return String(rounded);
}

function formatPointTooltipContent(
  sourceName: string,
  point: FleetRankingSeriesPoint,
  displayMode: FleetRankingDisplayMode,
  groupBy: FleetRankingGroupBy,
  valueTooltipLabel: string,
  meanRank: boolean
): string {
  const bucketHeader = fleetRankingGroupByTooltipHeader(groupBy);
  const rows = [
    `<tr><td>TEAM</td><td>${sourceName.toUpperCase()}</td></tr>`,
    `<tr><td>${bucketHeader}</td><td>${point.bucketLabel}</td></tr>`,
  ];

  if (displayMode === "ranking") {
    const rankText = meanRank
      ? formatMeanRank(point.yValue as number)
      : `${Math.round(point.yValue as number)}${point.rankPoolSize > 0 ? ` of ${point.rankPoolSize}` : ""}`;
    rows.push(`<tr><td>${meanRank ? "MEAN RANK" : "RANK"}</td><td>${rankText}</td></tr>`);
    if (point.avgVmgPercent != null && Number.isFinite(point.avgVmgPercent)) {
      rows.push(`<tr><td>${valueTooltipLabel}</td><td>${d3.format(".1f")(point.avgVmgPercent)}</td></tr>`);
    }
  } else {
    rows.push(`<tr><td>${valueTooltipLabel}</td><td>${d3.format(".1f")(point.yValue as number)}</td></tr>`);
  }

  rows.push(`<tr><td>SAMPLES</td><td>${point.sampleCount}</td></tr>`);
  return `<table class='table-striped'>${rows.join("")}</table>`;
}

function drawChart(
  container: HTMLDivElement,
  props: FleetRankingTrendChartProps,
  setTooltip: SetTooltip
): void {
  const width = Math.max(container.clientWidth, 1);
  const showLegend = props.showLegend ?? false;
  const dimOpacity = props.dimOpacity ?? DEFAULT_DIM_OPACITY;
  const valueAxisLabel = props.valueAxisLabel ?? "VMG %";
  const valueTooltipLabel = props.valueTooltipLabel ?? "VMG %";
  const height = Math.max(Math.min(width * 0.55, 560), 360);
  const margin = {
    top: 48,
    right: 72,
    bottom: showLegend ? 96 : 56,
    left: 56,
  };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  d3.select(container).selectAll("*").remove();

  const svg = d3
    .select(container)
    .append("svg")
    .attr("class", "fleet-ranking-trend-svg")
    .attr("width", width)
    .attr("height", height)
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("role", "img")
    .attr("aria-label", props.title);

  svg
    .append("text")
    .attr("class", "fleet-ranking-trend-title")
    .attr("x", width / 2)
    .attr("y", 24)
    .attr("text-anchor", "middle")
    .text(props.title);

  const plot = svg
    .append("g")
    .attr("class", "fleet-ranking-trend-plot")
    .attr("transform", `translate(${margin.left},${margin.top})`);

  const bucketKeys = props.buckets.map((b) => b.key);
  const xScale = d3
    .scalePoint<string>()
    .domain(bucketKeys)
    .range([0, innerWidth])
    .padding(0.5);

  const bucketLabelByKey = new Map(props.buckets.map((b) => [b.key, b.label]));

  const valueAxisMax = props.valueAxisMax;
  const valueAxisMin = props.valueAxisMin;
  const minSampleCount = props.minSampleCount;
  const finiteYValues = props.series.flatMap((s) =>
    s.points
      .filter(
        (p) =>
          p.yValue != null &&
          Number.isFinite(p.yValue) &&
          hasEnoughSamplesForChart(p.sampleCount, minSampleCount)
      )
      .map((p) =>
        props.displayMode === "value"
          ? clipPlotYValue(p.yValue as number, valueAxisMin, valueAxisMax)
          : (p.yValue as number)
      )
  );

  const plotY = (y: number): number =>
    props.displayMode === "value" ? clipPlotYValue(y, valueAxisMin, valueAxisMax) : y;

  let yScale: d3.ScaleLinear<number, number>;
  if (props.displayMode === "ranking") {
    const maxRank = finiteYValues.length > 0 ? Math.max(...finiteYValues) : 1;
    const top = Math.max(maxRank, 2);
    yScale = d3.scaleLinear().domain([top, 1]).range([innerHeight, 0]);
  } else {
    const minY = finiteYValues.length > 0 ? Math.min(...finiteYValues) : 0;
    const maxY = finiteYValues.length > 0 ? Math.max(...finiteYValues) : valueAxisMax ?? 100;
    const pad = Math.max((maxY - minY) * 0.08, 2);
    const domainMin =
      valueAxisMin != null
        ? Math.max(valueAxisMin, minY - pad)
        : props.valueAxisAllowNegative
          ? minY - pad
          : Math.max(0, minY - pad);
    const domainMax =
      valueAxisMax != null ? Math.min(valueAxisMax, maxY + pad) : maxY + pad;
    yScale = d3
      .scaleLinear()
      .domain([domainMin, domainMax])
      .range([innerHeight, 0]);
  }

  plot
    .append("g")
    .attr("class", "fleet-ranking-trend-grid")
    .call(
      d3
        .axisLeft(yScale)
        .tickSize(-innerWidth)
        .tickFormat(() => "")
    )
    .call((g) => g.select(".domain").remove())
    .call((g) => g.selectAll(".tick line").attr("class", "fleet-ranking-trend-grid-line"));

  plot
    .append("g")
    .attr("class", "fleet-ranking-trend-x-axis")
    .attr("transform", `translate(0,${innerHeight})`)
    .call(
      d3
        .axisBottom(xScale)
        .tickFormat((key) => bucketLabelByKey.get(String(key)) ?? String(key))
    )
    .selectAll("text")
    .attr("class", () =>
      props.onBucketClick
        ? "fleet-ranking-trend-axis-label is-clickable"
        : "fleet-ranking-trend-axis-label"
    )
    .attr("transform", bucketKeys.length > 8 ? "rotate(-35)" : null)
    .style("text-anchor", bucketKeys.length > 8 ? "end" : "middle")
    .attr("dx", bucketKeys.length > 8 ? "-0.4em" : "0")
    .attr("dy", bucketKeys.length > 8 ? "0.2em" : "0.7em")
    .on("click", (_event, key) => {
      if (!props.onBucketClick) return;
      const bucketKey = String(key);
      const bucketLabel = bucketLabelByKey.get(bucketKey) ?? bucketKey;
      props.onBucketClick(bucketKey, bucketLabel);
    });

  plot
    .append("g")
    .attr("class", "fleet-ranking-trend-y-axis")
    .call(d3.axisLeft(yScale).ticks(6).tickFormat((d) => formatYTick(d, props.displayMode)));

  const yAxisLabel =
    props.displayMode === "ranking"
      ? props.meanRank
        ? "Mean start rank (1 is best)"
        : "Team rank (1 is best)"
      : valueAxisLabel;
  plot
    .append("text")
    .attr("class", "fleet-ranking-trend-axis-title")
    .attr("transform", "rotate(-90)")
    .attr("x", -innerHeight / 2)
    .attr("y", -44)
    .attr("text-anchor", "middle")
    .text(yAxisLabel);

  const xAxisLabel = fleetRankingGroupByAxisLabel(props.groupBy);
  plot
    .append("text")
    .attr("class", "fleet-ranking-trend-axis-title")
    .attr("x", innerWidth / 2)
    .attr("y", innerHeight + (bucketKeys.length > 8 ? 72 : 48))
    .attr("text-anchor", "middle")
    .text(xAxisLabel);

  const line = d3
    .line<FleetRankingSeries["points"][number]>()
    .defined((d) => d.yValue != null && Number.isFinite(d.yValue))
    .x((d) => xScale(d.bucketKey) ?? 0)
    .y((d) => yScale(plotY(d.yValue as number)));

  for (const s of props.series) {
    const color = sourceColor(s.sourceName);
    const validPoints = s.points.filter(
      (p) =>
        p.yValue != null &&
        Number.isFinite(p.yValue) &&
        hasEnoughSamplesForChart(p.sampleCount, minSampleCount)
    );
    if (validPoints.length === 0) continue;

    const opacity = seriesOpacity(s.sourceName, props.selectedSourceNames, dimOpacity);
    const glow = needsLightGlow(s.sourceName);
    const seriesGroup = plot
      .append("g")
      .attr("class", `fleet-ranking-trend-series${glow ? " fleet-ranking-trend-series-glow" : ""}`)
      .attr("data-source", s.sourceName)
      .attr("opacity", opacity);

    if (glow) {
      seriesGroup
        .append("path")
        .datum(validPoints)
        .attr("class", "fleet-ranking-trend-line-glow")
        .attr("fill", "none")
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 4)
        .attr("stroke-opacity", 0.22)
        .attr("d", line);
    }

    seriesGroup
      .append("path")
      .datum(validPoints)
      .attr("class", "fleet-ranking-trend-line")
      .attr("fill", "none")
      .attr("stroke", color)
      .attr("stroke-width", 2)
      .attr("d", line);

    const sourceName = s.sourceName;
    const showTooltip = (event: MouseEvent, point: FleetRankingSeriesPoint): void => {
      setTooltip({
        visible: true,
        content: formatPointTooltipContent(
          sourceName,
          point,
          props.displayMode,
          props.groupBy,
          valueTooltipLabel,
          props.meanRank === true
        ),
        x: event.clientX + 12,
        y: event.clientY - 12,
      });
    };

    const hideTooltip = (): void => {
      setTooltip({ visible: false, content: "", x: 0, y: 0 });
    };

    seriesGroup
      .selectAll(`.fleet-ranking-trend-dot-${cssEscape(s.sourceName)}`)
      .data(validPoints)
      .enter()
      .append("circle")
      .attr("class", `fleet-ranking-trend-dot fleet-ranking-trend-dot-${cssEscape(s.sourceName)}${glow ? " fleet-ranking-trend-dot-glow" : ""}`)
      .attr("cx", (d) => xScale(d.bucketKey) ?? 0)
      .attr("cy", (d) => yScale(plotY(d.yValue as number)))
      .attr("r", 4)
      .attr("fill", color)
      .attr("stroke", glow ? "rgba(255, 255, 255, 0.75)" : "var(--color-bg-card, #fff)")
      .attr("stroke-width", glow ? 2 : 1.5)
      .on("mouseover", (event, d) => showTooltip(event, d))
      .on("mousemove", (event, d) => showTooltip(event, d))
      .on("mouseout", hideTooltip);

    const last = validPoints[validPoints.length - 1];
    seriesGroup
      .append("text")
      .attr("class", `fleet-ranking-trend-end-label${glow ? " fleet-ranking-trend-end-label-glow" : ""}`)
      .attr("x", (xScale(last.bucketKey) ?? 0) + 8)
      .attr("y", yScale(plotY(last.yValue as number)) + 4)
      .attr("fill", color)
      .text(s.sourceName.toUpperCase());
  }

  if (showLegend) {
    const legend = svg
      .append("g")
      .attr("class", "fleet-ranking-trend-legend")
      .attr("transform", `translate(${margin.left}, ${height - 28})`);

    const legendItemWidth = Math.min(120, innerWidth / Math.max(props.series.length, 1));
    const legendColumns = Math.max(1, Math.floor(innerWidth / legendItemWidth));
    props.series.forEach((s, index) => {
      const col = index % legendColumns;
      const row = Math.floor(index / legendColumns);
      const color = sourceColor(s.sourceName);
      const item = legend
        .append("g")
        .attr("class", "fleet-ranking-trend-legend-item")
        .attr("transform", `translate(${col * legendItemWidth}, ${row * 18})`);
      item
        .append("circle")
        .attr("r", 4)
        .attr("fill", color);
      item
        .append("text")
        .attr("class", "fleet-ranking-trend-legend-label")
        .attr("x", 10)
        .attr("y", 4)
        .text(s.sourceName.toUpperCase());
    });
  }
}

function cssEscape(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, "_");
}

export default function FleetRankingTrendChart(props: FleetRankingTrendChartProps) {
  let containerRef: HTMLDivElement | undefined;
  let resizeObserver: ResizeObserver | undefined;
  const [localTooltip, setLocalTooltip] = createSignal<TooltipState>({
    visible: false,
    content: "",
    x: 0,
    y: 0,
  });

  const render = (): void => {
    if (!containerRef) return;
    if (props.buckets.length === 0 || props.series.length === 0) {
      d3.select(containerRef).selectAll("*").remove();
      setLocalTooltip({ visible: false, content: "", x: 0, y: 0 });
      return;
    }
    drawChart(containerRef, props, setLocalTooltip);
  };

  onMount(() => {
    render();
    if (typeof ResizeObserver !== "undefined" && containerRef) {
      resizeObserver = new ResizeObserver(() => render());
      resizeObserver.observe(containerRef);
    } else {
      const onResize = (): void => render();
      window.addEventListener("resize", onResize);
      onCleanup(() => window.removeEventListener("resize", onResize));
    }
  });

  createEffect(() => {
    props.buckets;
    props.series;
    props.displayMode;
    props.groupBy;
    props.title;
    props.selectedSourceNames;
    props.showLegend;
    props.dimOpacity;
    props.valueAxisLabel;
    props.valueTooltipLabel;
    props.rankDirection;
    props.valueAxisAllowNegative;
    props.valueAxisMax;
    props.valueAxisMin;
    props.minSampleCount;
    props.onBucketClick;
    render();
  });

  onCleanup(() => {
    resizeObserver?.disconnect();
    setLocalTooltip({ visible: false, content: "", x: 0, y: 0 });
    if (containerRef) {
      d3.select(containerRef).selectAll("*").remove();
    }
  });

  return (
    <div class="fleet-ranking-trend-chart-wrap">
      <div
        ref={containerRef}
        class="fleet-ranking-trend-chart"
        data-display-mode={props.displayMode}
        data-group-by={props.groupBy}
      />
      <div
        class="tooltip fleet-ranking-trend-tooltip"
        classList={{ "fleet-ranking-trend-tooltip-visible": localTooltip().visible }}
        style={{
          left: `${localTooltip().x}px`,
          top: `${localTooltip().y}px`,
        }}
        innerHTML={sanitizeTooltipHtml(localTooltip().content)}
      />
    </div>
  );
}
